"use client"

import { useField } from "formik"
import type { ComponentProps } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

type FormInputProps = Omit<ComponentProps<"input">, "name"> & {
  name: string
  label: string
  description?: string
  showLength?: boolean
  containerClassName?: string
}

export function FormInput({
  name,
  label,
  description,
  showLength = false,
  containerClassName,
  id = name,
  ...props
}: FormInputProps) {
  const [field, meta] = useField<string>(name)
  const error = meta.touched && meta.error ? meta.error : null

  return (
    <div className={cn("space-y-1.5", containerClassName)}>
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        {showLength && typeof props.maxLength === "number" ? (
          <span className="text-xs text-muted-foreground">{field.value.length}/{props.maxLength}</span>
        ) : null}
      </div>
      <Input
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : description ? `${id}-description` : undefined}
        {...field}
        {...props}
      />
      {description ? (
        <p id={`${id}-description`} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="text-xs font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  )
}
